import React, { useEffect, useState } from "react";
import { Search, Eye } from "lucide-react";
import api, { eur, apiErr } from "@/lib/api";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { STATUS_LABELS, StatusBadge } from "@/pages/OrderTracking";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUSES = ["received", "payment_confirmed", "preparing", "shipped", "delivered", "cancelled", "refunded"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);

  const load = () => { const url = filter ? `/admin/orders?status=${filter}` : "/admin/orders"; api.get(url).then(({ data }) => setOrders(data)).catch(() => {}); };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const changeStatus = async (order, status) => {
    try {
      await api.put(`/admin/orders/${order.order_number}/status?status=${status}`);
      toast.success(`Κατάσταση: ${STATUS_LABELS[status]}`);
      load();
      if (sel && sel.order_number === order.order_number) setSel({ ...sel, status });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const filtered = orders.filter((o) => o.order_number.toLowerCase().includes(q.toLowerCase()) || o.email.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 lg:p-10">
      <AdminHeader title="Orders" subtitle="ΠΑΡΑΓΓΕΛΙΕΣ" />
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-2 bg-[#18181C] border border-zinc-700 rounded px-3 flex-1 max-w-xs">
          <Search className="w-4 h-4 text-zinc-500" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Αναζήτηση..." className="flex-1 bg-transparent py-2.5 text-sm outline-none" data-testid="admin-order-search" />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-[#18181C] border border-zinc-700 rounded px-3 py-2.5 text-sm" data-testid="admin-order-filter">
          <option value="">Όλες οι καταστάσεις</option>
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
      </div>

      <div className="card-surface rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="text-left text-zinc-500 font-mono text-xs uppercase border-b border-zinc-800">
            <th className="p-4">Παραγγελία</th><th className="p-4">Πελάτης</th><th className="p-4">Ημ/νία</th><th className="p-4">Σύνολο</th><th className="p-4">Πληρωμή</th><th className="p-4">Κατάσταση</th><th className="p-4"></th>
          </tr></thead>
          <tbody className="divide-y divide-zinc-800">
            {filtered.map((o) => (
              <tr key={o.id} className="hover:bg-white/[0.02]" data-testid={`admin-order-row-${o.order_number}`}>
                <td className="p-4 font-mono font-bold">#{o.order_number}</td>
                <td className="p-4"><p>{o.customer.first_name} {o.customer.last_name}</p><p className="text-xs text-zinc-500">{o.email}</p></td>
                <td className="p-4 text-zinc-400 text-xs">{new Date(o.created_at).toLocaleDateString("el-GR")}</td>
                <td className="p-4 font-semibold">{eur(o.total)}</td>
                <td className="p-4 text-xs uppercase font-mono">{o.payment_method === "cod" ? "Αντικαταβολή" : "Κάρτα"}<br /><span className={o.payment_status === "paid" ? "text-green-500" : "text-yellow-500"}>{o.payment_status}</span></td>
                <td className="p-4">
                  <select value={o.status} onChange={(e) => changeStatus(o, e.target.value)} className="bg-[#0A0A0B] border border-zinc-700 rounded px-2 py-1.5 text-xs" data-testid={`admin-order-status-${o.order_number}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                  </select>
                </td>
                <td className="p-4"><button onClick={() => setSel(o)} className="p-2 hover:text-[#FF2E4C]" data-testid={`admin-order-view-${o.order_number}`}><Eye className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="p-8 text-center text-zinc-500 text-sm">Δεν υπάρχουν παραγγελίες.</p>}
      </div>

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent className="bg-[#121215] border-zinc-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          {sel && <>
            <DialogHeader><DialogTitle className="font-display uppercase flex items-center justify-between">#{sel.order_number} <StatusBadge status={sel.status} /></DialogTitle></DialogHeader>
            <div className="space-y-4 text-sm">
              <div className="card-surface rounded p-3">
                <p className="font-semibold">{sel.customer.first_name} {sel.customer.last_name}</p>
                <p className="text-zinc-400">{sel.email} · {sel.customer.phone}</p>
                <p className="text-zinc-400">{sel.customer.address}, {sel.customer.city} {sel.customer.postal_code}, {sel.customer.country}</p>
                <p className="text-zinc-400 mt-1">{sel.shipping_method} · {sel.payment_method === "cod" ? "Αντικαταβολή" : "Κάρτα"}</p>
                {sel.notes && <p className="text-zinc-500 mt-1 italic">"{sel.notes}"</p>}
              </div>
              <div className="space-y-2">
                {sel.items.map((it, i) => (
                  <div key={i} className="flex gap-3 items-center"><img src={it.image} alt="" className="w-12 h-14 object-cover rounded" /><div className="flex-1"><p className="font-semibold text-xs">{it.name}</p><p className="text-[10px] font-mono text-zinc-500 uppercase">{it.color}/{it.size} x{it.quantity}</p></div><span className="text-xs">{eur(it.price * it.quantity)}</span></div>
                ))}
              </div>
              <div className="border-t border-zinc-800 pt-3 space-y-1">
                <div className="flex justify-between text-zinc-400"><span>Υποσύνολο</span><span>{eur(sel.subtotal)}</span></div>
                {sel.discount > 0 && <div className="flex justify-between text-[#FF2E4C]"><span>Έκπτωση</span><span>-{eur(sel.discount)}</span></div>}
                <div className="flex justify-between text-zinc-400"><span>Μεταφορικά</span><span>{eur(sel.shipping_cost)}</span></div>
                <div className="flex justify-between font-display font-black"><span>ΣΥΝΟΛΟ</span><span>{eur(sel.total)}</span></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select value={sel.status} onChange={(e) => changeStatus(sel, e.target.value)} className="bg-[#0A0A0B] border border-zinc-700 rounded px-2 py-2 text-xs" data-testid="admin-order-detail-status">
                  {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
                {sel.payment_status === "paid" && sel.status !== "refunded" && (
                  <button onClick={() => changeStatus(sel, "refunded")} className="bg-red-600/20 text-red-400 rounded px-2 py-2 text-xs font-bold uppercase hover:bg-red-600/30" data-testid="admin-order-refund">Επιστροφή Χρημάτων</button>
                )}
              </div>
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
